import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Spacer, Tab, Tabs, useSnackbar } from '@emburse/embark-core';
import { IFrameModal } from '@emburse/embark-complex';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { dayJsFormatDate } from '../../util/dateAdapter';
import ERPGatewayHeader from './ERPGatewayHeader';
import ERPGatewayNoConfiguration from './ERPGatewayNoConfiguration';
import ERPGatewayHistoryImported from './ERPGatewayHistoryImported';
import ERPGatewayHistoryExported from './ERPGatewayHistoryExported';
import ERPGatewayScheduleDrawer from './ERPGatewayScheduleDrawer';
import ERPGatewayExportErrors from './ERPGatewayExportErrors';
import ERPGatewayMapping from './ERPGatewayMapping';
import ERPGatewayModal from './components/ERPGatewayModal';
import ERPGatewayPendingExports from './ERPGatewayPendingExports';
import { getJobRun, getJobRunExpRptErrors, getJobRunResultById } from '../../api/jobRun';
import { getExpenseReports } from '../../api/expenseReport';
import { getNexusNetSuiteSync, getNexusNetSuiteActionPending, getNexusNetSuiteExport, getNetSuiteObjectMap } from '../../api/nexusNetSuiteJob';
import { getNexusServiceConfiguration, getNexusToken, createCompanyNexusOnboarding } from '../../api/nexusService';
import { ERPGatewayTypes, erpModalUrl } from '../../interfaces/ERPGateway';
import { poll } from '../../util/helpers';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { $t, useTranslation } from '../../hooks/useTranslation';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import useAccountConfig from '../../hooks/useAccountConfig';
import { ERPGatewayExportProvider } from './ERPGatewayExportProvider';
import { isProd } from '../../util/environment';
import { logger } from '../../util/logger';
// Initialize dayjs plugins
dayjs.extend(utc);
const transformedForDataGrid = (data) => data.map((item) => ({
    id: item.Id || item.ExpRptId,
    ...item
}));
function ERPGateway({ erpType, configText }) {
    useTranslation();
    const mappingComponentRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const { accountConfig } = useAccountConfig();
    const [openConfigureModal, setOpenConfigureModal] = useState(false);
    const [configureModalUrl, setConfigureModalUrl] = useState('');
    const [hasErrors, setHasErrors] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [openScheduleDrawer, setOpenScheduleDrawer] = useState(false);
    const [importedData, setImportedData] = useState([]);
    const [exportedData, setExportedData] = useState([]);
    const [importedDataLoading, setImportedDataLoading] = useState(false);
    const [exportedDataLoading, setExportedDataLoading] = useState(false);
    const [hasGatewayCompany, setHasGatewayCompany] = useState(false);
    const [hasGatewayCompanyLoading, setHasGatewayCompanyLoading] = useState(true);
    const [, setCompanyApiKey] = useState(''); // companyApiKey - save incase is needed later
    const [configuration, setConfiguration] = useState(null);
    const [isSyncPending, setIsSyncPending] = useState(false);
    const [exportErrors, setExportErrors] = useState([]);
    const [expenseReports, setExpenseReports] = useState([]);
    const defaultModalProps = useMemo(() => ({
        onConfirm: () => { },
        onClose: () => { },
        open: false,
        header: 'Confirmation',
        body: '',
        confirmButtonText: 'OK',
        consfirmButtonDisabled: false,
        showCancel: true
    }), []);
    const [modalProps, setModalProps] = useState(defaultModalProps);
    const capitalizedErp = erpType.charAt(0).toUpperCase() + erpType.slice(1);
    const snackbarHandler = useCallback((message, severity) => {
        enqueueSnackbar(message, { variant: 'alert', AlertProps: { severity } });
    }, [enqueueSnackbar]);
    const catchError = useCallback((err) => {
        removeLoadingState();
        snackbarHandler(err?.Message || err, 'error');
    }, [removeLoadingState, snackbarHandler]);
    const getTokenAndOpenModal = useCallback(async () => {
        applyLoadingState(30000);
        try {
            const response = await getNexusToken();
            const responseJSON = await response.json();
            if (isProd) {
                setConfigureModalUrl(`${erpModalUrl[erpType].prod}${responseJSON.Token}`);
            }
            else {
                setConfigureModalUrl(`${erpModalUrl[erpType].test}${responseJSON.Token}`);
            }
            removeLoadingState();
            setOpenConfigureModal(true);
        }
        catch (err) {
            catchError(err);
        }
    }, [applyLoadingState, catchError, erpType, removeLoadingState]);
    const createConfiguration = async () => {
        applyLoadingState(60000, '', $t('Initializing company configuration. Please wait.'));
        try {
            await createCompanyNexusOnboarding(erpType);
            removeLoadingState();
            getTokenAndOpenModal();
        }
        catch (err) {
            catchError($t('Unable to initialize company configuration. Please contact support.'));
        }
    };
    const getJobRunExpRptErrorsData = useCallback(async () => {
        let responseJSON = null;
        try {
            const response = await getJobRunExpRptErrors();
            responseJSON = await response.json();
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [catchError]);
    const getJobRunData = useCallback(async (type) => {
        let responseJSON = null;
        try {
            const response = await getJobRun(type);
            responseJSON = await response.json();
            return responseJSON;
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [catchError]);
    const checkForExportErrors = useCallback(async () => {
        const errorsData = await getJobRunExpRptErrorsData();
        if (errorsData && errorsData.length > 0) {
            setExportErrors(errorsData);
            setHasErrors(true);
        }
        else {
            setExportErrors([]);
            setHasErrors(false);
        }
    }, [getJobRunExpRptErrorsData]);
    const pollExportData = useCallback(async () => {
        const POLLING_INTERVAL = 10000;
        const POLLING_MAX_ATTEMPTS = 60;
        let parsedData = [];
        try {
            await poll(() => getJobRunData(`${erpType}-export`), (result) => {
                parsedData = JSON.parse(JSON.stringify(result));
                const hasPending = parsedData.some((item) => item.Status.toLowerCase().includes('pending'));
                return hasPending;
            }, POLLING_INTERVAL, POLLING_MAX_ATTEMPTS);
            setExportedData(transformedForDataGrid(parsedData.reverse()));
            checkForExportErrors();
        }
        catch (err) {
            logger.log(err);
        }
    }, [checkForExportErrors, erpType, getJobRunData]);
    const getImportData = useCallback(async () => {
        setImportedDataLoading(true);
        const importJobRunData = await getJobRunData(`${erpType}-import`);
        if (importJobRunData) {
            setImportedData(transformedForDataGrid(importJobRunData.reverse()));
        }
        setImportedDataLoading(false);
    }, [erpType, getJobRunData]);
    const getExportData = useCallback(async () => {
        setExportedDataLoading(true);
        const jobRunData = await getJobRunData(`${erpType}-export`);
        if (jobRunData) {
            setExportedData(transformedForDataGrid(jobRunData.reverse()));
            checkForExportErrors();
            if (accountConfig.UIFlags.EnableNexusNetsuiteExportPolling) {
                const hasPending = jobRunData.some((item) => item.Status.toLowerCase().includes('pending'));
                if (hasPending) {
                    pollExportData();
                }
            }
        }
        setExportedDataLoading(false);
    }, [
        erpType,
        accountConfig.UIFlags.EnableNexusNetsuiteExportPolling,
        checkForExportErrors,
        getJobRunData,
        pollExportData
    ]);
    const doSync = useCallback(async () => {
        applyLoadingState(30000);
        try {
            const response = await getNexusNetSuiteSync();
            await response.json();
            getImportData();
            removeLoadingState();
        }
        catch (err) {
            catchError(err);
        }
    }, [applyLoadingState, catchError, getImportData, removeLoadingState]);
    const doNetSuiteExport = useCallback(async (expRptIds) => {
        applyLoadingState(30000);
        try {
            const response = expRptIds && expRptIds.length
                ? await getNexusNetSuiteExport(expRptIds)
                : await getNexusNetSuiteExport();
            await response.json();
            getExportData();
            removeLoadingState();
        }
        catch (err) {
            catchError(err);
        }
    }, [applyLoadingState, catchError, getExportData, removeLoadingState]);
    const getIsSyncPending = useCallback(async () => {
        let responseJSON = null;
        try {
            const response = await getNexusNetSuiteActionPending();
            responseJSON = await response.json();
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [catchError]);
    const pollSyncPending = useCallback(async () => {
        const POLLING_INTERVAL = 10000;
        const POLLING_MAX_ATTEMPTS = 60;
        try {
            await poll(() => getIsSyncPending(), (result) => {
                const parsedData = JSON.parse(JSON.stringify(result));
                return parsedData.length > 0;
            }, POLLING_INTERVAL, POLLING_MAX_ATTEMPTS);
            setIsSyncPending(false);
            getImportData();
            getExportData();
        }
        catch (err) {
            logger.log(err);
        }
    }, [getExportData, getImportData, getIsSyncPending]);
    const hasSyncPending = useCallback(async () => {
        try {
            const hasSync = await getIsSyncPending();
            setIsSyncPending(hasSync.length > 0);
            if (hasSync.length > 0) {
                pollSyncPending();
            }
        }
        catch (err) {
            catchError(err);
        }
    }, [catchError, getIsSyncPending, pollSyncPending]);
    useEffect(() => {
        if (hasGatewayCompany) {
            getImportData();
            getExportData();
            hasSyncPending();
        }
    }, [hasGatewayCompany, getExportData, getImportData, hasSyncPending]);
    const setInactiveModal = useCallback(() => {
        setModalProps({
            onConfirm: () => {
                setModalProps(defaultModalProps);
                getTokenAndOpenModal();
            },
            onClose: () => setModalProps(defaultModalProps),
            open: true,
            header: `The ${capitalizedErp} Sync is Currently Inactive`,
            body: `Return to the Sync Settings page to verify your configuration and re-activate your sync.
            Please note that Scheduled syncs will not occur while the sync is inactive.`,
            confirmButtonText: 'Settings',
            consfirmButtonDisabled: false,
            showCancel: false
        });
    }, [capitalizedErp, defaultModalProps, getTokenAndOpenModal]);
    const fetchNetSuiteObjectMap = useCallback(async () => {
        let responseJSON = null;
        try {
            const response = await getNetSuiteObjectMap(null, null, accountConfig?.Emp.Company.PK, 'Company');
            responseJSON = await response.json();
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [accountConfig?.Emp.Company.PK, catchError]);
    const getConfiguration = useCallback(async () => {
        let responseJSON = null;
        try {
            const response = await getNexusServiceConfiguration();
            responseJSON = await response.json();
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [catchError]);
    const saveConfiguration = useCallback(async () => {
        try {
            const config = await getConfiguration();
            setConfiguration(config);
        }
        catch (err) {
            catchError(err);
        }
    }, [catchError, getConfiguration]);
    const getNetSuiteObjectMapData = useCallback(async (modalClose) => {
        applyLoadingState();
        const netSuiteObjectMapData = await fetchNetSuiteObjectMap();
        if (netSuiteObjectMapData?.NetSuiteIsActive) {
            setHasGatewayCompany(true);
        }
        if (netSuiteObjectMapData?.NetSuiteObjectId) {
            setCompanyApiKey(netSuiteObjectMapData.NetSuiteObjectId);
            const config = await getConfiguration();
            setConfiguration(config);
            if (modalClose && !!config && config.SyncRequired) {
                snackbarHandler($t(`You added the Emburse ${capitalizedErp} Integration. Set a schedule to start using it.`), 'success');
                await getNexusNetSuiteSync(true);
                if (mappingComponentRef.current) {
                    mappingComponentRef.current.getCardsAndMap();
                }
                hasSyncPending();
            }
            if (!config) {
                setInactiveModal();
            }
        }
        removeLoadingState();
        setHasGatewayCompanyLoading(false);
    }, [
        applyLoadingState,
        fetchNetSuiteObjectMap,
        getConfiguration,
        hasSyncPending,
        removeLoadingState,
        setInactiveModal,
        snackbarHandler,
        capitalizedErp
    ]);
    const fetchExpenseReports = useCallback(async () => {
        try {
            const opptions = {
                Processed: true,
                APSyncComplete: false,
                GLSyncComplete: false,
                IncludeEmpName: true
            };
            const response = await getExpenseReports(opptions);
            const responseJSON = await response.json();
            setExpenseReports(responseJSON);
        }
        catch (err) {
            catchError(err);
        }
    }, [catchError]);
    useEffect(() => {
        getNetSuiteObjectMapData();
        fetchExpenseReports();
    }, [fetchExpenseReports, getNetSuiteObjectMapData]);
    const onTabChange = (_e, val) => {
        setTabIndex(val);
        hasSyncPending();
    };
    const onConfigureModalClose = async (_e, reason) => {
        if (reason === 'backdropClick') {
            // do something? Probably wont need this
        }
        if (reason === 'escapeKeyDown') {
            // do something? Probably wont need this
        }
        if (reason === 'headerClose') {
            getNetSuiteObjectMapData();
        }
        if (reason === 'iframeTriggered') {
            await getNetSuiteObjectMapData(true);
        }
        setOpenConfigureModal(false);
    };
    const getJobRunResult = useCallback(async (id) => {
        let responseJSON = null;
        try {
            const response = await getJobRunResultById(id);
            responseJSON = await response.json();
            return responseJSON;
        }
        catch (err) {
            catchError(err);
        }
        return responseJSON;
    }, [catchError]);
    const downLoadFile = (data, date, type) => {
        // Parse the UTC date string and convert to local time
        const formattedDate = dayjs.utc(date).local().tz(dayjs.tz.guess());
        const timestamp = dayJsFormatDate(formattedDate, 'YYYY-MMM-DD-hh_mm_ss_A');
        const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);
        const erp = erpType === ERPGatewayTypes.NetSuite ? 'NetSuite' : capitalizedErp;
        const file = new Blob([data], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = `${erp}_${capitalizedType}_${timestamp}.txt`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const downloadFileHandler = async (id, type) => {
        let data = await getJobRunResult(id);
        let date;
        if (type === 'export') {
            const record = exportedData.find((item) => item.Id === id);
            date = record.StartDate;
            data = JSON.parse(data);
            if (data.ExpenseReports) {
                // Remove escape characters
                const expRpts = Object.entries(data.ExpenseReports).reduce((acc, [key, value]) => {
                    const newVal = value;
                    acc[key] = newVal.includes('ExportId') ? JSON.parse(newVal) : newVal;
                    return acc;
                }, {});
                data.ExpenseReports = expRpts;
            }
            data = JSON.stringify({ data }, null, '\t');
        }
        else {
            const record = importedData.find((item) => item.Id === id);
            date = record.StartDate;
            data = JSON.parse(data);
            data = JSON.stringify({ data }, null, '\t');
        }
        downLoadFile(data, date, type);
    };
    const canShowDetailsInHistory = (dateString) => {
        if (accountConfig.UIFlags.EnableNexusNetsuiteImportExportDownload) {
            return dayjs(dateString).isAfter(dayjs(accountConfig.UIFlags.EnableNexusNetsuiteImportExportDownload));
        }
        return false;
    };
    return (_jsxs(SingleColumnLayout, { children: [_jsx(IFrameModal, { open: openConfigureModal, onClose: onConfigureModalClose, url: configureModalUrl }), _jsx(ERPGatewayModal, { ...modalProps }), _jsx(ERPGatewayScheduleDrawer, { open: openScheduleDrawer, isActiveStatus: !!configuration, isSyncPending: isSyncPending, onCancel: () => {
                    setOpenScheduleDrawer(false);
                }, onSave: () => {
                    setOpenScheduleDrawer(false);
                }, openUpdated: () => {
                    setOpenScheduleDrawer(false);
                }, catchError: catchError, onImportSync: async () => {
                    await doSync();
                    setOpenScheduleDrawer(false);
                    hasSyncPending();
                }, onExportSync: async () => {
                    await doNetSuiteExport();
                    setOpenScheduleDrawer(false);
                    hasSyncPending();
                } }), _jsx(ERPGatewayHeader, { erpType: erpType, hasCompany: hasGatewayCompany, isActiveStatus: !!configuration, isSyncPending: isSyncPending, openConfigureModal: getTokenAndOpenModal, openSchedule: () => {
                    setOpenScheduleDrawer(true);
                } }), _jsx(Spacer, { size: 50 }), !hasGatewayCompany && !hasGatewayCompanyLoading && (_jsx(ERPGatewayNoConfiguration, { onConfigure: createConfiguration, configText: configText })), hasGatewayCompany && (_jsxs(_Fragment, { children: [_jsxs(Tabs, { id: "CorpNetSuiteConfig", value: tabIndex, onChange: onTabChange, children: [_jsx(Tab, { label: $t('History'), size: "medium" }), _jsx(Tab, { label: $t('Mapping'), iconPosition: "end", size: "medium" }), accountConfig.UIFlags.EnableErpGatewayPendingExports && (_jsx(Tab, { label: $t('Pending Exports'), size: "medium" })), hasGatewayCompany && (_jsx(Tab, { label: $t('Export Errors'), icon: hasErrors && (_jsx(Badge, { color: "error", style: { marginRight: '20px' }, variant: "dot" })), iconPosition: "start", size: "medium" }))] }), _jsx(Spacer, { size: 50 }), tabIndex === 0 && (_jsxs(_Fragment, { children: [_jsx(ERPGatewayHistoryImported, { data: importedData, loading: importedDataLoading, canDownload: accountConfig.UIFlags.EnableNexusNetsuiteFileDownload, onDownloadfile: downloadFileHandler, canShowDetails: canShowDetailsInHistory }), _jsx(Spacer, { size: 50 }), _jsx(ERPGatewayHistoryExported, { data: exportedData, loading: exportedDataLoading, hasErrors: hasErrors, canDownload: accountConfig.UIFlags.EnableNexusNetsuiteFileDownload, onDownloadfile: downloadFileHandler, canShowDetails: canShowDetailsInHistory, onViewErrors: () => {
                                    onTabChange(null, accountConfig.UIFlags.EnableErpGatewayPendingExports ? 3 : 2);
                                } })] })), tabIndex === 1 && (_jsx(ERPGatewayMapping, { catchError: catchError, snackbarHandler: snackbarHandler, isSyncPending: isSyncPending, configuration: configuration, ref: mappingComponentRef, getConfiguration: saveConfiguration })), tabIndex === 2 && accountConfig.UIFlags.EnableErpGatewayPendingExports && (_jsx(ERPGatewayExportProvider, { getExportData: fetchExpenseReports, catchError: catchError, children: _jsx(ERPGatewayPendingExports, { isActiveStatus: !!configuration, isSyncPending: isSyncPending, data: transformedForDataGrid(expenseReports) }) })), tabIndex ===
                        (accountConfig.UIFlags.EnableErpGatewayPendingExports ? 3 : 2) && (_jsx(ERPGatewayExportProvider, { getExportData: getExportData, catchError: catchError, doNetSuiteExport: doNetSuiteExport, children: _jsx(ERPGatewayExportErrors, { hasErrors: hasErrors, isActiveStatus: !!configuration, isSyncPending: isSyncPending, onNoErrors: () => setHasErrors(false), exportErrors: transformedForDataGrid(exportErrors) }) }))] }))] }));
}
export default ERPGateway;
